import { useState, useMemo, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  createDocumentRecord, 
  updateDocumentRecord, 
  deleteDocumentRecord,
  uploadFile,
  DOCUMENT_CATEGORIES
} from '../lib/api';
import { useDocuments } from '../hooks/useApiQueries';
import { 
  FileText, 
  Plus, 
  MagnifyingGlass, 
  FunnelSimple,
  Download,
  Trash,
  PencilSimple,
  Link as LinkIcon,
  Copy,
  Check,
  X,
  CaretDown,
  House,
  User,
  Buildings,
  FolderOpen,
  Eye,
  CloudArrowUp,
  Tag,
  Globe,
  Lock
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';

// Type definitions
interface Document {
  id: string;
  name?: string;
  file_name?: string;
  category?: string;
  assigned_to_user?: string;
  assigned_to_apartment?: string;
  assigned_to_property?: string;
  file_url?: string;
  description?: string;
  created_at?: string;
  updated_at?: string;
  tags?: string[];
  file_size?: number;
  visibility?: 'internal' | 'public';
}

interface Category {
  id: string;
  name?: string;
}

interface UserType {
  id: string;
  name?: string;
  email?: string;
}

interface Apartment {
  id: string;
  apartment_number?: string;
  name?: string;
}

interface Property {
  id: string;
  name?: string;
}

export default function DocumentsPage() {
  const { user } = useAuth();
  
  // React Query for documents data
  const { data: documentsData, isLoading: loading, refetch: refetchDocuments } = useDocuments(
    user?.organization_id,
    true
  );
  
  // Extract data from React Query response
  const documents: Document[] = documentsData?.documents || [];
  const categories: string[] = documentsData?.categories || [];
  const users: UserType[] = documentsData?.users || [];
  const apartments: Apartment[] = documentsData?.apartments || [];
  const properties: Property[] = documentsData?.properties || [];
  
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterAssignment, setFilterAssignment] = useState('all');
  
  // Dialog states
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null);
  const [uploading, setUploading] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    category: string;
    newCategory: string;
    file: File | null;
    tags: string;
    visibility: 'internal' | 'public';
  }>({
    name: '',
    description: '',
    category: '',
    newCategory: '',
    file: null,
    tags: '',
    visibility: 'internal'
  });
  
  // Assignment form
  const [assignForm, setAssignForm] = useState({
    type: 'user', // 'user', 'apartment', 'property'
    id: ''
  });

  // Refetch helper
  const fetchData = useCallback(() => {
    refetchDocuments();
  }, [refetchDocuments]);

  // Filter and search documents
  const filteredDocuments = useMemo(() => {
    let filtered = [...documents];
    
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(d => 
        d.name?.toLowerCase().includes(query) ||
        d.description?.toLowerCase().includes(query) ||
        d.category?.toLowerCase().includes(query)
      );
    }
    
    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(d => d.category === filterCategory);
    }
    
    // Assignment filter
    if (filterAssignment === 'unassigned') {
      filtered = filtered.filter(d => !d.assigned_to_user && !d.assigned_to_apartment && !d.assigned_to_property);
    } else if (filterAssignment === 'users') {
      filtered = filtered.filter(d => d.assigned_to_user);
    } else if (filterAssignment === 'apartments') {
      filtered = filtered.filter(d => d.assigned_to_apartment);
    } else if (filterAssignment === 'properties') {
      filtered = filtered.filter(d => d.assigned_to_property);
    }
    
    return filtered.sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime());
  }, [documents, searchQuery, filterCategory, filterAssignment]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData(prev => ({ 
        ...prev, 
        file,
        name: prev.name || file.name.replace(/\.[^/.]+$/, '') // Use filename without extension as default name
      }));
    }
  };

  const handleUpload = async () => {
    if (!formData.file) {
      toast.error('Välj en fil att ladda upp');
      return;
    }
    
    const category = formData.newCategory || formData.category;
    if (!category) {
      toast.error('Välj eller ange en kategori');
      return;
    }
    
    setUploading(true);
    try {
      // Upload file to CommHub
      const uploadResult = await uploadFile(formData.file);
      
      // Create document record
      const docData = {
        name: formData.name || formData.file.name,
        description: formData.description,
        category: category,
        file_url: uploadResult.url,
        file_name: formData.file.name,
        file_size: formData.file.size,
        file_type: formData.file.type,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        organization_id: user?.organization_id,
        uploaded_by: user?.id,
        uploaded_by_name: user?.name,
        visibility: formData.visibility
      };
      
      await createDocumentRecord(docData);
      
      // New category will be added via refetch
      toast.success('Dokument uppladdat');
      setShowUploadDialog(false);
      setFormData({ name: '', description: '', category: '', newCategory: '', file: null, tags: '', visibility: 'internal' });
      fetchData();
    } catch (error) {
      console.error('Upload failed:', error);
      toast.error('Kunde inte ladda upp dokument');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = async () => {
    if (!selectedDocument) return;
    
    try {
      const category = formData.newCategory || formData.category;
      await updateDocumentRecord(selectedDocument.id, {
        name: formData.name,
        description: formData.description,
        category: category,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        visibility: formData.visibility
      });
      
      // New category will be reflected via refetch
      toast.success('Dokument uppdaterat');
      setShowEditDialog(false);
      setSelectedDocument(null);
      fetchData();
    } catch (error) {
      toast.error('Kunde inte uppdatera dokument');
    }
  };

  const handleDelete = async (doc: Document) => {
    if (!confirm(`Vill du verkligen ta bort "${doc.name}"?`)) return;
    
    try {
      await deleteDocumentRecord(doc.id);
      toast.success('Dokument borttaget');
      fetchData();
    } catch (error) {
      toast.error('Kunde inte ta bort dokument');
    }
  };

  const handleAssign = async () => {
    if (!selectedDocument || !assignForm.id) {
      toast.error('Välj vad dokumentet ska tilldelas');
      return;
    }
    
    try {
      const updateData: Partial<Document> = {};
      if (assignForm.type === 'user') {
        updateData.assigned_to_user = assignForm.id;
        updateData.assigned_to_apartment = undefined;
        updateData.assigned_to_property = undefined;
      } else if (assignForm.type === 'apartment') {
        updateData.assigned_to_user = undefined;
        updateData.assigned_to_apartment = assignForm.id;
        updateData.assigned_to_property = undefined;
      } else {
        updateData.assigned_to_user = undefined;
        updateData.assigned_to_apartment = undefined;
        updateData.assigned_to_property = assignForm.id;
      }
      
      await updateDocumentRecord(selectedDocument.id, updateData);
      toast.success('Dokument tilldelat');
      setShowAssignDialog(false);
      setSelectedDocument(null);
      setAssignForm({ type: 'user', id: '' });
      fetchData();
    } catch (error) {
      toast.error('Kunde inte tilldela dokument');
    }
  };

  const copyLink = (doc: Document) => {
    if (doc.file_url) {
      navigator.clipboard.writeText(doc.file_url);
      setCopiedId(doc.id);
      toast.success('Länk kopierad');
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const openEditDialog = (doc: Document) => {
    setSelectedDocument(doc);
    setFormData({
      name: doc.name || '',
      description: doc.description || '',
      category: doc.category || '',
      newCategory: '',
      file: null,
      tags: doc.tags?.join(', ') || '',
      visibility: doc.visibility || 'internal'
    });
    setShowEditDialog(true);
  };

  const openAssignDialog = (doc: Document) => {
    setSelectedDocument(doc);
    setAssignForm({
      type: doc.assigned_to_user ? 'user' : doc.assigned_to_apartment ? 'apartment' : doc.assigned_to_property ? 'property' : 'user',
      id: doc.assigned_to_user || doc.assigned_to_apartment || doc.assigned_to_property || ''
    });
    setShowAssignDialog(true);
  };

  const getAssignmentInfo = (doc: Document) => {
    if (doc.assigned_to_user) {
      const u = users.find(u => u.id === doc.assigned_to_user);
      return { type: 'user', icon: User, label: u?.name || 'Användare', color: 'bg-blue-100 text-blue-700' };
    }
    if (doc.assigned_to_apartment) {
      const a = apartments.find(a => a.id === doc.assigned_to_apartment);
      return { type: 'apartment', icon: House, label: `Lgh ${a?.apartment_number || ''}`, color: 'bg-green-100 text-green-700' };
    }
    if (doc.assigned_to_property) {
      const p = properties.find(p => p.id === doc.assigned_to_property);
      return { type: 'property', icon: Buildings, label: p?.name || 'Fastighet', color: 'bg-purple-100 text-purple-700' };
    }
    return null;
  };

  const formatFileSize = (bytes: number | undefined) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const canEdit = user?.role === 'superadmin' || user?.role === 'admin' || user?.role === 'board_member';

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-48 bg-slate-200 rounded" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <div key={i} className="h-32 bg-slate-200 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="documents-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-blue-100 flex items-center justify-center">
            <FolderOpen size={20} className="text-blue-600" weight="fill" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Dokument</h1>
            <p className="text-sm text-slate-500">{documents.length} dokument</p>
          </div>
        </div>
        {canEdit && (
          <Button onClick={() => setShowUploadDialog(true)} data-testid="upload-document-btn">
            <CloudArrowUp size={18} className="mr-2" />
            Ladda upp
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            placeholder="Sök dokument..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="document-search"
          />
        </div>
        <Select value={filterCategory} onValueChange={setFilterCategory}>
          <SelectTrigger className="w-[180px]" data-testid="category-filter">
            <FunnelSimple size={16} className="mr-2" />
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla kategorier</SelectItem>
            {categories.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAssignment} onValueChange={setFilterAssignment}>
          <SelectTrigger className="w-[180px]" data-testid="assignment-filter">
            <SelectValue placeholder="Tilldelning" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alla</SelectItem>
            <SelectItem value="unassigned">Otilldelade</SelectItem>
            <SelectItem value="users">Användare</SelectItem>
            <SelectItem value="apartments">Lägenheter</SelectItem>
            <SelectItem value="properties">Fastigheter</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Documents Grid */}
      {filteredDocuments.length === 0 ? (
        <div className="card-flat">
          <div className="empty-state py-12">
            <FileText size={48} className="empty-state-icon" />
            <p className="text-slate-500">Inga dokument hittades</p>
            {canEdit && (
              <Button variant="outline" size="sm" className="mt-4" onClick={() => setShowUploadDialog(true)}>
                <Plus size={16} className="mr-2" />
                Ladda upp första dokumentet
              </Button>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredDocuments.map(doc => {
            const assignment = getAssignmentInfo(doc);
            
            return (
              <div 
                key={doc.id} 
                className="card-flat hover:shadow-md transition-shadow"
                data-testid={`document-${doc.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <FileText size={20} className="text-slate-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-slate-900 truncate">{doc.name}</h3>
                    <p className="text-xs text-slate-500 truncate">{doc.file_name}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-600 rounded">
                        {doc.category}
                      </span>
                      {doc.file_size && (
                        <span className="text-xs text-slate-400">{formatFileSize(doc.file_size)}</span>
                      )}
                    </div>
                  </div>
                </div>
                
                {doc.description && (
                  <p className="text-sm text-slate-600 mt-3 line-clamp-2">{doc.description}</p>
                )}
                
                {/* Tags */}
                {doc.tags && doc.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {doc.tags.map((tag) => (
                      <span key={tag} className="text-xs px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded flex items-center gap-1">
                        <Tag size={10} />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                
                {/* Assignment badge */}
                {assignment && assignment.icon && (
                  <div className={`mt-3 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded ${assignment.color}`}>
                    <assignment.icon size={12} />
                    {assignment.label}
                  </div>
                )}
                
                {/* Actions */}
                <div className="flex items-center gap-1 mt-4 pt-3 border-t border-slate-100">
                  <a 
                    href={doc.file_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="p-2 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700"
                    title="Öppna"
                  >
                    <Eye size={16} />
                  </a>
                  <a 
                    href={doc.file_url} 
                    download={doc.file_name}
                    className="p-2 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700"
                    title="Ladda ner"
                  >
                    <Download size={16} />
                  </a>
                  <button
                    onClick={() => copyLink(doc)}
                    className="p-2 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700"
                    title="Kopiera länk"
                  >
                    {copiedId === doc.id ? <Check size={16} className="text-green-500" /> : <Copy size={16} />}
                  </button>
                  {canEdit && (
                    <>
                      <button
                        onClick={() => openAssignDialog(doc)}
                        className="p-2 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700"
                        title="Tilldela"
                      >
                        <LinkIcon size={16} />
                      </button>
                      <button
                        onClick={() => openEditDialog(doc)}
                        className="p-2 hover:bg-slate-100 rounded text-slate-500 hover:text-slate-700"
                        title="Redigera"
                      >
                        <PencilSimple size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(doc)}
                        className="p-2 hover:bg-red-100 rounded text-slate-500 hover:text-red-600 ml-auto"
                        title="Ta bort"
                      >
                        <Trash size={16} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={showUploadDialog} onOpenChange={setShowUploadDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ladda upp dokument</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Fil *</Label>
              <Input 
                type="file" 
                onChange={handleFileChange}
                className="mt-1"
                data-testid="document-file-input"
              />
            </div>
            <div>
              <Label>Namn</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Dokumentnamn"
                className="mt-1"
              />
            </div>
            <div>
              <Label>Beskrivning</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Kort beskrivning..."
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label>Kategori *</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, category: v, newCategory: '' }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Välj kategori" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                  {/* Custom categories */}
                  {categories.filter(c => !DOCUMENT_CATEGORIES.find(dc => dc.id === c)).map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={formData.newCategory}
                onChange={(e) => setFormData(prev => ({ ...prev, newCategory: e.target.value, category: '' }))}
                placeholder="...eller skriv in egen kategori"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Synlighet</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    checked={formData.visibility === 'internal'}
                    onChange={() => setFormData(prev => ({ ...prev, visibility: 'internal' }))}
                    className="w-4 h-4"
                  />
                  <Lock size={16} className="text-slate-500" />
                  <span className="text-sm">Internt (endast inloggade)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="visibility"
                    checked={formData.visibility === 'public'}
                    onChange={() => setFormData(prev => ({ ...prev, visibility: 'public' }))}
                    className="w-4 h-4"
                  />
                  <Globe size={16} className="text-green-600" />
                  <span className="text-sm">Publikt (visas på portalen)</span>
                </label>
              </div>
            </div>
            <div>
              <Label>Taggar (kommaseparerade)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                placeholder="t.ex. viktigt, 2024, styrelse"
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowUploadDialog(false)}>Avbryt</Button>
              <Button onClick={handleUpload} disabled={uploading}>
                {uploading ? 'Laddar upp...' : 'Ladda upp'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Redigera dokument</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Namn</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Beskrivning</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="mt-1"
                rows={2}
              />
            </div>
            <div>
              <Label>Kategori</Label>
              <Select 
                value={formData.category} 
                onValueChange={(v) => setFormData(prev => ({ ...prev, category: v, newCategory: '' }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Välj kategori" />
                </SelectTrigger>
                <SelectContent>
                  {DOCUMENT_CATEGORIES.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                  ))}
                  {categories.filter(c => !DOCUMENT_CATEGORIES.find(dc => dc.id === c)).map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={formData.newCategory}
                onChange={(e) => setFormData(prev => ({ ...prev, newCategory: e.target.value, category: '' }))}
                placeholder="...eller skriv in egen kategori"
                className="mt-2"
              />
            </div>
            <div>
              <Label>Synlighet</Label>
              <div className="flex gap-4 mt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="edit-visibility"
                    checked={formData.visibility === 'internal'}
                    onChange={() => setFormData(prev => ({ ...prev, visibility: 'internal' }))}
                    className="w-4 h-4"
                  />
                  <Lock size={16} className="text-slate-500" />
                  <span className="text-sm">Internt</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="edit-visibility"
                    checked={formData.visibility === 'public'}
                    onChange={() => setFormData(prev => ({ ...prev, visibility: 'public' }))}
                    className="w-4 h-4"
                  />
                  <Globe size={16} className="text-green-600" />
                  <span className="text-sm">Publikt</span>
                </label>
              </div>
            </div>
            <div>
              <Label>Taggar (kommaseparerade)</Label>
              <Input
                value={formData.tags}
                onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Avbryt</Button>
              <Button onClick={handleEdit}>Spara</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={showAssignDialog} onOpenChange={setShowAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Tilldela dokument</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label>Tilldela till</Label>
              <Select value={assignForm.type} onValueChange={(v) => setAssignForm({ type: v, id: '' })}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Användare</SelectItem>
                  <SelectItem value="apartment">Lägenhet</SelectItem>
                  <SelectItem value="property">Fastighet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>
                {assignForm.type === 'user' && 'Välj användare'}
                {assignForm.type === 'apartment' && 'Välj lägenhet'}
                {assignForm.type === 'property' && 'Välj fastighet'}
              </Label>
              <Select value={assignForm.id} onValueChange={(v) => setAssignForm(prev => ({ ...prev, id: v }))}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Välj..." />
                </SelectTrigger>
                <SelectContent>
                  {assignForm.type === 'user' && users.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>
                  ))}
                  {assignForm.type === 'apartment' && apartments.map(a => (
                    <SelectItem key={a.id} value={a.id}>Lgh {a.apartment_number}</SelectItem>
                  ))}
                  {assignForm.type === 'property' && properties.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={() => {
                  // Clear assignment
                  if (selectedDocument) {
                    updateDocumentRecord(selectedDocument.id, {
                      assigned_to_user: undefined,
                      assigned_to_apartment: undefined,
                      assigned_to_property: undefined
                    }).then(() => {
                      toast.success('Tilldelning borttagen');
                      setShowAssignDialog(false);
                      fetchData();
                    });
                  }
                }}
              >
                Ta bort tilldelning
              </Button>
              <Button onClick={handleAssign}>Tilldela</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
